/**
 * Responsibility: the ranked shortlist as a table, with the warnings visible
 * in the row rather than hidden behind a click.
 *
 * The column order is the argument the page is making. Card, then what it
 * costs raw, then what a gem fetches, then the multiple, then the payoff - so
 * a row reads left to right as "buy this for X, sell for Y, make Z". The
 * warning column sits immediately after the profit, because the profit is the
 * number that will be believed and the warning is the reason not to.
 *
 * Rows are not re-sorted here. The hub already ordered them - sound rows
 * first, then by payoff - and a client-side sort that reordered them would
 * silently discard that, putting a sharply falling card back at the top
 * exactly because its stale median makes it look best.
 *
 * The vertical cap is lifted on phones, as in WatchTable: a bounded inner
 * scroller on a narrow screen makes this page scroll differently from every
 * other one.
 */
import { useState } from 'react';
import { NameSortHeader } from '../shelf/NameSortHeader';
import { headerNode } from '../shelf/headerNode';
import { nameSortValue } from '../shelf/nameSort';
import type { NameSort } from '../shelf/nameSort';
import { sortableNumber } from '../shelf/sortValues';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import StarIcon from '@mui/icons-material/Star';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import WarningIcon from '@mui/icons-material/WarningAmber';
import { DataTable } from '@kit/components/data';
import type { DataColumn } from '@kit/components/data';
import { ToolbarIconButton } from '@kit/components/buttons';
import { EMPTY } from '@kit/utils';
import type { Opportunity } from '../../api/client';
import { PriceCell } from './PriceCell';
import { VerifyLinks } from './VerifyLinks';
import { money, multiple, percent, signedMoney, toNumber } from './money';

type OpportunityTableProps = {
  rows: Opportunity[];
  loading?: boolean;
  busy?: boolean;
  onRefresh: (row: Opportunity) => void;
  onToggleWatched: (row: Opportunity) => void;
  onRemove: (row: Opportunity) => void;
  onOpen: (row: Opportunity) => void;
};

/** See WatchTable: effectively no cap, without patching the kit. */
const UNCAPPED = 100_000;

const THESIS_LABEL: Record<string, string> = {
  SCARCE_GEM: 'Low pop',
  BIG_MULTIPLE: 'Big multiple',
  SCARCE_AND_VALUABLE: 'Low pop + multiple',
};

function CardName({ row }: { row: Opportunity }) {
  const subtitle = [row.set_name, row.card_number].filter(Boolean).join(' · ');
  return (
    <Stack spacing={0} sx={{ minWidth: 0 }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {row.card_name}
      </Typography>
      {subtitle ? (
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      ) : (
        <Typography variant="caption" color="warning.main">
          no set named - prices may be a different printing
        </Typography>
      )}
    </Stack>
  );
}

/**
 * The warnings, as icons with the full sentence in the tooltip.
 *
 * Icons rather than text because there can be three of them and the table has
 * to stay readable, but every one is hoverable and the same sentences appear
 * in full in the detail panel, so nothing is only ever a symbol.
 */
function Warnings({ row }: { row: Opportunity }) {
  const flags: Array<{ key: string; icon: React.ReactNode; title: string }> = [];

  if (row.is_falling_sharply) {
    flags.push({
      key: 'falling',
      icon: <TrendingDownIcon fontSize="small" color="error" />,
      title: `Sold prices are falling sharply (${percent(row.trend_ratio, 0)} of their earlier level). Grading takes months and this payoff assumes today's price.`,
    });
  }
  if (!row.is_sold_backed) {
    flags.push({
      key: 'asking',
      icon: <WarningIcon fontSize="small" color="warning" />,
      title: 'Priced on asking prices only - no completed sales were found, and listings run high.',
    });
  }
  if (row.needs_a_gem) {
    flags.push({
      key: 'lottery',
      icon: <WarningIcon fontSize="small" color="warning" />,
      title: 'Loses money on average at this card’s gem rate. It only pays if a card actually comes back a 10.',
    });
  }

  if (flags.length === 0) {
    return (
      <Typography variant="caption" color="success.main">
        clear
      </Typography>
    );
  }

  return (
    <Stack direction="row" spacing={0.5}>
      {flags.map((flag) => (
        // The title carried the explanation and is gone with the
        // tooltip; the label keeps it reachable to a screen reader.
        <span key={flag.key} aria-label={flag.title}>
          {flag.icon}
        </span>
      ))}
    </Stack>
  );
}

/**
 * Column widths are measured, not guessed: headings never wrap, so a
 * heading that does not fit its column is clipped. `tests/table-headers.mjs`
 * renders this table in a browser and fails on a clipped one.
 */
export function OpportunityTable({
  rows,
  loading = false,
  busy = false,
  onRefresh,
  onToggleWatched,
  onRemove,
  onOpen,
}: OpportunityTableProps) {
  // Which of the two name orderings the card column uses.
  const [nameSort, setNameSort] = useState<NameSort>('alphabetical');
  const columns: DataColumn<Opportunity>[] = [
    {
      id: 'card_name',
      sortValue: (row: Opportunity) => nameSortValue(`${row.card_name} #${row.card_number}`, nameSort),
      label: headerNode(
        <NameSortHeader label="Card" value={nameSort} onChange={setNameSort} />,
      ),
      render: (row) => <CardName row={row} />,
    },
    {
      id: 'raw',
      sortValue: (row: Opportunity) => sortableNumber(row.raw_price),
      label: 'Raw',
      width: 110,
      render: (row) => <PriceCell price={row.raw_price} absent="none" />,
    },
    {
      id: 'psa10',
      sortValue: (row: Opportunity) => sortableNumber(row.psa10_price),
      label: 'PSA 10',
      width: 110,
      render: (row) => <PriceCell price={row.psa10_price} absent="none" />,
    },
    {
      id: 'multiple',
      sortValue: (row: Opportunity) => sortableNumber(row.multiple),
      label: 'Mult',
      width: 92,
      align: 'right',
      render: (row) => multiple(row.multiple),
    },
    {
      id: 'profit',
      sortValue: (row: Opportunity) => sortableNumber(row.single_gem.profit),
      label: `Profit on ${rows[0]?.single_gem.cards_bought ?? 5}`,
      width: 130,
      align: 'right',
      render: (row) => (
        <Typography
          variant="body2"
          sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}
          color={(toNumber(row.single_gem.profit) ?? 0) > 0 ? 'success.main' : 'text.primary'}
        >
          {signedMoney(row.single_gem.profit)}
        </Typography>
      ),
    },
    {
      id: 'warnings',
      label: 'Trust',
      width: 90,
      render: (row) => <Warnings row={row} />,
    },
    {
      id: 'break_even',
      sortValue: (row: Opportunity) => sortableNumber(row.single_gem.break_even_raw_price),
      label: 'Pay up to',
      width: 122,
      align: 'right',
      render: (row) => (
        <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {money(row.single_gem.break_even_raw_price)}
        </Typography>
      ),
    },
    {
      id: 'gem_rate',
      sortValue: (row: Opportunity) => sortableNumber(row.population?.gem_rate),
      label: 'Gem %',
      width: 106,
      align: 'right',
      render: (row) =>
        row.population?.gem_rate ? (
          percent(row.population.gem_rate)
        ) : (
          <Typography variant="body2" color="text.disabled">
            {EMPTY}
          </Typography>
        ),
    },
    {
      id: 'verify',
      label: 'Check',
      width: 150,
      render: (row) => <VerifyLinks card={row} variant="row" />,
    },
    {
      id: 'thesis',
      label: 'Why',
      width: 150,
      render: (row) => (
        <Chip size="small" variant="outlined" label={THESIS_LABEL[row.thesis] ?? row.thesis} />
      ),
    },
    {
      id: 'actions',
      label: '',
      width: 140,
      align: 'right',
      render: (row) => (
        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
          <ToolbarIconButton
            aria-label={row.watched ? 'Unflag' : 'Flag for a submission'}
            onClick={() => onToggleWatched(row)}
            disabled={busy}
          >
            {row.watched ? <StarIcon fontSize="small" /> : <StarOutlineIcon fontSize="small" />}
          </ToolbarIconButton>
          <ToolbarIconButton
            aria-label="Re-price from eBay"
            onClick={() => onRefresh(row)}
            disabled={busy}
          >
            <RefreshIcon fontSize="small" />
          </ToolbarIconButton>
          <ToolbarIconButton
            aria-label="Remove from the list"
            onClick={() => onRemove(row)}
            disabled={busy}
          >
            <DeleteIcon fontSize="small" />
          </ToolbarIconButton>
        </Stack>
      ),
    },
  ];

  return (
    <Stack spacing={1} sx={{ minHeight: 0, flexGrow: 1 }}>
      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(row) => row.id}
        loading={loading}
        emptyMessage="Nothing clears the costs yet. Add a card, or refresh to re-price what is here."
        maxHeight={{ xs: UNCAPPED, md: 620 }}
        onRowClick={onOpen}
      />
    </Stack>
  );
}
